import type { Item, Project, ItemStatus } from "@/lib/types";
import { STATUS_ORDER, STATUS_META } from "@/lib/types";
import { deadlineState, dueLabel, shortDate } from "@/lib/format";
import { useIsMobile } from "@/lib/useMediaQuery";
import { useSetItemStatus } from "@/hooks/useData";
import { blockingLabel, riskOf } from "@/lib/deps";
import { TypeBadge, PriorityDot, Dot, GBadge, rowTitle } from "./atoms";
import { stripLinks, withLinks } from "@/lib/links";
import { TaskTimeControls } from "./TaskTimeControls";
import { ParentHint, SubtaskList } from "./SubtaskList";
import { useTimer } from "@/context/TimerContext";

export function itemProjectId(item: any): string | undefined {
  return item?.expand?.phase?.expand?.project?.id ?? item?.expand?.phase?.project;
}

export function ItemRow({
  item, projectById, onToggle, onOpen, showProject = true, itemsById, subCounts, variant = "card", stacked = false,
  subtasks, childrenOf, parent,
}: {
  item: Item;
  projectById: Map<string, Project>;
  onToggle: (item: Item) => void;
  onOpen: (item: Item) => void;
  showProject?: boolean;
  itemsById?: Map<string, Item>;
  subCounts?: Map<string, { done: number; total: number }>;
  // "card" (default): individually bordered, rounded, on --surface, with a gap
  // between rows — the list reads as a stack of cards. "flush": the old
  // edge-to-edge table row with a bottom hairline, for callers that still want
  // rows packed inside one container.
  variant?: "card" | "flush";
  /** The phone's two-line row on the desktop too — for a narrow column such as
   *  Today's task sidebar, where the one-line row has no room for the title. */
  stacked?: boolean;
  /** Todoist subtasks to draw inside this row (see lib/hierarchy listNesting). */
  subtasks?: Item[];
  childrenOf?: (i: Item) => Item[];
  /** The Todoist parent of a subtask listed apart from it. */
  parent?: Item;
}) {
  const mobile = useIsMobile();
  const project = projectById.get(itemProjectId(item) || "");
  /**
   * Two dates, each read from its own field.
   *
   * This used to show `scheduledFor(item)` — `start || due` — which meant a
   * task with no Todoist date borrowed its DEADLINE and displayed it in the
   * scheduled-day slot. The two are not interchangeable: one is when you plan
   * to work on it, the other is when it has to be finished, and showing a
   * deadline where a start date belongs misreports both.
   *
   * `scheduledFor` is still right for FILTERING — Today and Upcoming must catch
   * a task that has only a deadline, or it goes missing entirely. It is only
   * display that needs the fields kept apart.
   */
  const due = dueLabel(item.start, "", item.status);
  const deadline = deadlineState(item);
  const deadlineText = item.due ? shortDate(item.due) : "";
  const isDone = item.status === "done";
  const blocked = itemsById ? blockingLabel(item, itemsById) : undefined;
  const sub = subCounts?.get(item.id);
  const isCard = variant === "card";
  // §12 / §15 — on the phone the slip-risk message rides on the card as an
  // amber line; the desktop shows it in the drawer banner and the register.
  const risk = mobile && itemsById ? riskOf(item, itemsById) : undefined;
  // The task the timer is on is tinted, so it can be found in a long list.
  const timer = useTimer();
  const timing = timer.running && timer.meta.itemId === item.id;
  const timingStyle = timing ? { background: "var(--accentTint2)", ...(isCard ? { borderColor: "var(--accentLine)" } : null) } : null;
  const nested = subtasks && subtasks.length > 0 && childrenOf
    ? <SubtaskList items={subtasks} childrenOf={childrenOf} onOpen={onOpen} onToggle={onToggle} touch={mobile} />
    : null;

  /**
   * The phone row is built, not wrapped.
   *
   * The desktop row is one flex line with `flex-wrap` and a handful of `order`
   * overrides for the phone, and the result was whatever the widths happened to
   * produce: a long title pushed the timer buttons onto a line of their own, a
   * short one left them stranded mid-row. Two explicit lines — title, then
   * everything about the task underneath — behave the same whatever the title
   * turns out to be.
   */
  if (mobile || stacked) {
    return (
      <div
        onClick={() => onOpen(item)}
        className="item-row"
        style={{
          display: "flex", alignItems: "flex-start", gap: 10,
          padding: "var(--row-py-m) 14px", cursor: "pointer",
          ...(isCard
            ? { border: "1px solid var(--line)", borderRadius: 12, background: "var(--surface)" }
            : { borderBottom: "1px solid var(--tint2)" }),
          ...timingStyle,
        }}
      >
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(item); }}
          style={{
            width: 20, height: 20, flex: "none", marginTop: 1, borderRadius: "50%", cursor: "pointer",
            border: `1.5px solid ${isDone ? "var(--good)" : blocked ? "var(--warnLine)" : "var(--muted3)"}`,
            background: isDone ? "var(--good)" : "transparent", color: "var(--onAccent)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11,
          }}
          aria-label={isDone ? "Mark incomplete" : "Mark complete"}
        >
          {isDone ? "✓" : ""}
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          {parent && <ParentHint parent={parent} />}
          <div style={{
            ...rowTitle, fontSize: 14.5, overflowWrap: "anywhere",
            textDecoration: isDone ? "line-through" : "none",
            color: isDone ? "var(--muted)" : "var(--ink)",
          }}>{withLinks(item.title, item.id)}</div>

          <div style={{
            display: "flex", alignItems: "center", flexWrap: "wrap", gap: "3px 8px", marginTop: 3,
            fontSize: 11.5, color: "var(--ink3)",
          }}>
            {showProject && project && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, minWidth: 0, maxWidth: "100%" }}>
                <Dot color={project.color} size={6} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{project.name}</span>
              </span>
            )}
            {due.text && (
              <span className="mono" style={{ color: due.overdue ? "var(--bad)" : "var(--ink3)" }}>{due.text}</span>
            )}
            {/* Shown whenever there IS a deadline, with the date on it — a
                chip reading "deadline soon" and nothing else made you open the
                task to find out when. */}
            {deadline && (
              <span className="mono" title={`Deadline ${deadlineText}`} style={{
                padding: "1px 6px", borderRadius: 20, fontSize: 10,
                background: deadline === "overdue" ? "var(--badTint)" : deadline === "later" ? "var(--tint)" : "var(--warnTint)",
                color: deadline === "overdue" ? "var(--bad)" : deadline === "later" ? "var(--muted)" : "var(--warn)",
              }}>⚑ {deadlineText}</span>
            )}
            {item.type !== "task" && <TypeBadge type={item.type} />}
            {sub && sub.total > 0 && (
              <span className="mono" style={{ fontSize: 10.5, color: "var(--muted)" }}>
                {(sub as { source?: string }).source === "children" ? "▣" : "☑"} {sub.done}/{sub.total}
              </span>
            )}
            {blocked && (
              <span style={{ color: "var(--warn)" }}>⛌ waits for {truncate(blocked, 18)}</span>
            )}
          </div>

          {risk && (
            <div style={{ fontSize: 11.5, color: "var(--warn)", marginTop: 3 }}>
              <span aria-hidden="true">⚠</span> {risk.message}
            </div>
          )}
          {nested}
        </div>

        <span style={{ flex: "none", display: "inline-flex", marginTop: -2 }}>
          <TaskTimeControls item={item} mobile />
        </span>
      </div>
    );
  }

  // With subtasks, the card's frame moves out to hold the row and the
  // subtasks beneath it, and the row itself stays one line.
  const frame: React.CSSProperties = {
    ...(isCard
      ? { border: "1px solid var(--line)", borderRadius: 12, background: "var(--surface)" }
      : { borderBottom: "1px solid var(--tint2)" }),
    ...timingStyle,
  };
  const row = (
    <div
      onClick={() => onOpen(item)}
      className={isCard && !nested ? "item-row row-hover" : "item-row"}
      style={{
        display: "flex", alignItems: "center", gap: mobile ? 10 : 12,
        padding: mobile ? "var(--row-py-m) 14px" : `var(--row-py) ${isCard ? 16 : 18}px`,
        cursor: "pointer",
        flexWrap: mobile ? "wrap" : "nowrap",
        ...(nested ? null : frame),
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(item); }}
        title={blocked ? `Waiting on "${blocked}"` : undefined}
        style={{
          width: 18, height: 18, flex: "none", borderRadius: "50%", cursor: "pointer",
          border: `1.5px solid ${isDone ? "var(--good)" : blocked ? "var(--warnLine)" : "var(--muted3)"}`,
          background: isDone ? "var(--good)" : "transparent", color: "var(--onAccent)",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10,
        }}
        aria-label={isDone ? "Mark incomplete" : "Mark complete"}
      >
        {isDone ? "✓" : ""}
      </button>

      {!mobile && <TypeBadge type={item.type} />}

      {/* The title leads on the phone. It used to sit on its own line UNDER the
          checkbox, the timer buttons and a badge reading "Task" — three pieces
          of chrome ahead of the one thing you are reading the row for. */}
      {/* A floor, not a preference. `flex: 1` gives the title a basis of 0, so
          once the badges, project, status and date overflow the row it is the
          title — the only part you actually read — that collapses to nothing.
          With a basis the project name truncates instead. */}
      <span style={{
        flex: mobile ? "1 1 auto" : "1 1 140px", minWidth: 0, fontSize: 14, order: mobile ? 1 : 0,
        display: "flex", alignItems: "center", gap: 8,
        textDecoration: isDone ? "line-through" : "none", color: isDone ? "var(--muted)" : "var(--ink)",
        overflow: "hidden", whiteSpace: mobile ? "normal" : "nowrap",
      }}>
        {parent && (
          <span title={`Subtask of "${stripLinks(parent.title)}"`} style={{ flex: "0 4 auto", minWidth: 0, maxWidth: 180, fontSize: 11.5, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            ↳ {stripLinks(parent.title)} ·
          </span>
        )}
        <span
          title={stripLinks(item.title)}
          style={{ ...rowTitle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: mobile ? "normal" : "nowrap" }}
        >{withLinks(item.title, item.id)}</span>
        {sub && sub.total > 0 && (
          <span
            className="mono"
            title={(sub as { source?: string }).source === "children" ? "Tasks complete" : "Checklist progress"}
            style={{ flex: "none", fontSize: 10.5, color: "var(--muted)", display: "inline-flex", alignItems: "center", gap: 3 }}
          >
            {(sub as { source?: string }).source === "children" ? "▣" : "☑"} {sub.done}/{sub.total}
          </span>
        )}
      </span>

      {risk && (
        <span style={{ flex: "1 1 100%", order: 7, fontSize: 11.5, color: "var(--warn)", display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span aria-hidden="true">⚠</span>{risk.message}
        </span>
      )}

      {blocked && (
        <span title={`Waiting on "${blocked}"`} style={{ flex: "none", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--warn)", order: mobile ? 6 : 0 }}>
          ⛌ waits for {truncate(blocked, mobile ? 14 : 22)}
        </span>
      )}

      {!mobile && !blocked && <PriorityDot priority={item.priority} />}
      {!mobile && item.syncEnabled && <GBadge />}

      {showProject && project && (
        <span style={{ display: "flex", alignItems: "center", gap: 7, maxWidth: mobile ? "auto" : 150, order: mobile ? 4 : 0, minWidth: 0 }}>
          <Dot color={project.color} size={6} />
          <span style={{ fontSize: 12, color: "var(--ink3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{project.name}</span>
        </span>
      )}

      {/* Kept on the title's line, not below it: parked on its own row the two
          buttons made every task three lines tall. */}
      <span style={{ order: mobile ? 2 : 0, marginLeft: mobile ? "auto" : 0, display: "inline-flex", flex: "none" }}>
        <TaskTimeControls item={item} mobile={mobile} />
      </span>

      {/* Every row on this screen is a task; saying so on each one is noise.
          Deliverable and milestone mean something, so those still show. */}
      {mobile
        ? item.type !== "task" && <span style={{ order: 3 }}><TypeBadge type={item.type} /></span>
        : <StatusSelect item={item} />}

      {/* Two cells, not one. The deadline chip used to live inside the date
          cell's 82px, which was sized for a date alone — adding a second date
          to it wrapped every row onto two lines. */}
      <span className="mono" style={{
        width: mobile ? "auto" : 74, textAlign: "right", whiteSpace: "nowrap",
        fontSize: 11, color: due.overdue ? "var(--bad)" : "var(--ink3)", order: mobile ? 5 : 0,
      }}>{due.text}</span>

      {!mobile && (
        <span
          title={deadline ? `Deadline ${deadlineText}` : undefined}
          className="mono"
          style={{
            flex: "none", width: 74, textAlign: "right", whiteSpace: "nowrap", fontSize: 10,
            color: deadline === "overdue" ? "var(--bad)" : deadline === "later" ? "var(--muted2)" : deadline ? "var(--warn)" : "transparent",
          }}
        >{deadline ? `⚑ ${deadlineText}` : "·"}</span>
      )}
    </div>
  );
  if (!nested) return row;
  return (
    <div className={isCard ? "row-hover" : undefined} style={frame}>
      {row}
      <div style={{ padding: `0 ${isCard ? 16 : 18}px 8px 46px` }}>{nested}</div>
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

// Quick status change without opening the editor. Styled to read like the old
// StatusPill but is a real <select>; stops propagation so it doesn't open the row.
function StatusSelect({ item }: { item: Item }) {
  const setStatus = useSetItemStatus();
  const meta = STATUS_META[item.status];
  return (
    <select
      value={item.status}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => { e.stopPropagation(); setStatus.mutate({ id: item.id, status: e.target.value as ItemStatus }); }}
      title="Change status"
      style={{
        flex: "none", appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
        padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 500, cursor: "pointer",
        border: "none", background: meta.bg, color: meta.text, maxWidth: 150,
      }}
    >
      {STATUS_ORDER.map((s) => (
        <option key={s} value={s} style={{ background: "var(--surface)", color: "var(--ink)" }}>{STATUS_META[s].label}</option>
      ))}
    </select>
  );
}
